import React, { useMemo, useRef, useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { toast } from 'react-toastify';
import { Job, JobStatus } from '../../job-summary/Interfaces';
import { FeedEmailData } from '../../mail/components/EmailFeedContent';
import { CustomerWithJobs } from '../utils/customerListTypes';
import { relTime } from '../../../themes/terminal/relTime';
import './TerminalCustomerCard.scss';

/**
 * TerminalCustomerCard — the log-line-format card that renders in the
 * Terminal theme. Drop-in replacement for CustomerCard: takes the same
 * prop surface (so ActiveCustomerList can swap it in without changes).
 *
 * Design goals:
 *  - Ball-in-court signaling is the headline, not status
 *  - Status is text, not a colored pill
 *  - Email thread is last 3 entries, not 25
 *  - Relative time on a single scale, column-prefixed like a log
 *  - One click = one destination; no ambiguous "card body" click
 */

// ── Status options exposed in the inline dropdown. 'Active' is intentionally
// omitted (the timer feature that owned it was removed); legacy Active jobs
// still render correctly because STATUS text is just whatever the job carries.
const SELECTABLE_STATUSES: JobStatus[] = [
  'Open',
  'Pending',
  'Awaiting Client Response',
  'Overdue',
  'Closed',
  'Cancelled',
  'Archived',
];

// How many emails to show under each job in the mini log.
const EMAIL_LOG_LIMIT = 6;

// Cutoff after which an inbound-unanswered email flips from "amber" to "red".
const STALE_INBOUND_MS = 48 * 60 * 60 * 1000;

// relTime is imported from '../../../themes/terminal/relTime' — the single
// source of truth for every timestamp in the terminal UI. The local
// helpers below (WEEKDAYS / shortDate) are retained because formatDue
// uses them for due-date formatting which has different semantics
// ("due in 3d" / "due Tue") than a generic relative timestamp.

const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function shortDate(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// ── Ball-in-court classifier ─────────────────────────────────────────────
// Looks at the most recent email on the job and decides who holds the ball.
//   'waiting'  — last inbound, <48h old  (amber: we owe a reply soon)
//   'stale'    — last inbound, ≥48h old  (red: we've been slow)
//   'replied'  — last outbound            (neutral: their turn)
//   'quiet'    — no emails                (neutral: fresh job, no context)
type BallState = 'waiting' | 'stale' | 'replied' | 'quiet';

function ballInCourt(emails: FeedEmailData[]): BallState {
  if (!emails || emails.length === 0) return 'quiet';
  // Sorted newest first already in most call sites, but be safe
  const sorted = [...emails].sort((a, b) => {
    const at = a.createdAt?.getTime?.() ?? 0;
    const bt = b.createdAt?.getTime?.() ?? 0;
    return bt - at;
  });
  const latest = sorted[0];
  const isOutbound = latest.folder === 'sent' || latest.emailType === 'sent';
  if (isOutbound) return 'replied';
  const age = Date.now() - (latest.createdAt?.getTime?.() ?? Date.now());
  return age >= STALE_INBOUND_MS ? 'stale' : 'waiting';
}

function countInboundUnread(emails: FeedEmailData[], dismissedAt?: Date | null): number {
  // Treat every inbound email since the last outbound as "unread to act on".
  // That's a closer proxy for "things that need you" than Metronic's read
  // flag, which reflects whether the user clicked it in the inbox.
  //
  // When dismissedAt is set (user clicked [×] on the badge), also ignore
  // inbound emails at or before that timestamp — they're treated as
  // "acknowledged out-of-band" so the badge clears. New inbound emails
  // after the dismissal still bring the count back.
  if (!emails || emails.length === 0) return 0;
  const sorted = [...emails].sort((a, b) => {
    const at = a.createdAt?.getTime?.() ?? 0;
    const bt = b.createdAt?.getTime?.() ?? 0;
    return bt - at;
  });
  const dismissMs = dismissedAt?.getTime?.() ?? 0;
  let count = 0;
  for (const e of sorted) {
    const isOutbound = e.folder === 'sent' || e.emailType === 'sent';
    if (isOutbound) break;
    const emailMs = e.createdAt?.getTime?.() ?? 0;
    if (dismissMs && emailMs <= dismissMs) break;
    count++;
  }
  return count;
}

// Parse the ISO timestamp stored on the job. Defensive — returns null if
// the field is missing or malformed so the count logic just falls back
// to "no dismissal".
function parseDismissedAt(job: Job): Date | null {
  const raw = (job as any).attentionDismissedAt;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

// ── Due date chip formatter ──────────────────────────────────────────────
// "due in 3d" / "due today" / "overdue 2d" / "—" if no due date.
function formatDue(dueDate: string | undefined): {text: string; tone: BallState | 'neutral'} {
  if (!dueDate) return { text: '—', tone: 'neutral' };
  const due = new Date(dueDate);
  if (isNaN(due.getTime())) return { text: '—', tone: 'neutral' };
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDue   = new Date(due.getFullYear(),   due.getMonth(),   due.getDate());
  const days = Math.round((startOfDue.getTime() - startOfToday.getTime()) / 86400000);

  if (days < 0)  return { text: `overdue ${Math.abs(days)}d`, tone: 'stale' };
  if (days === 0) return { text: 'due today',                  tone: 'waiting' };
  if (days === 1) return { text: 'due tomorrow',               tone: 'waiting' };
  if (days < 7)  return { text: `due ${WEEKDAYS[due.getDay()]}`, tone: days <= 3 ? 'waiting' : 'neutral' };
  if (days < 28) return { text: `due in ${days}d`,              tone: 'neutral' };
  return { text: `due ${shortDate(due)}`,                       tone: 'neutral' };
}

// ── Pretty display for a job status (lowercase, terminal-y) ──────────────
function statusText(status: JobStatus): string {
  // Existing labels are title-cased ("Awaiting Client Response") — cramped
  // in this dense layout. Normalize to short lowercase tokens.
  switch (status) {
    case 'Awaiting Client Response': return 'awaiting';
    case 'Active':   return 'active';
    case 'Open':     return 'open';
    case 'Pending':  return 'pending';
    case 'Overdue':  return 'overdue';
    case 'Closed':   return 'closed';
    case 'Cancelled':return 'cancelled';
    case 'Archived': return 'archived';
    default: return String(status).toLowerCase();
  }
}

// ── Email snippet extractor ──────────────────────────────────────────────
function snippetFor(email: FeedEmailData): string {
  const subj = (email.subject || '').trim();
  const msg  = (email.message || email.text || '').trim();
  // Drop quoted tails (lines starting with > or "On ... wrote:") — a bit
  // heuristic but removes the biggest noise source in reply chains.
  const cleaned = msg.split(/\n/).filter(l => !/^(>|On\s.+wrote:)/i.test(l.trim())).join(' ');
  if (cleaned.length > 4) return cleaned.slice(0, 90) + (cleaned.length > 90 ? '…' : '');
  return subj || '(no subject)';
}

// ─────────────────────────────────────────────────────────────────────────

export interface TerminalCustomerCardProps {
  customer: CustomerWithJobs;
  jobEmailsMap: Record<string, FeedEmailData[]>;
  updatingLastContactedFor: string | null;
  showStatusDropdown: string | null;
  onJobClick: (e: React.MouseEvent<HTMLButtonElement>, job: Job) => void;
  onDateClick: (e: React.MouseEvent<HTMLElement>, job: Job) => void;
  onOpenStatusModal: (job: Job, e: React.MouseEvent) => void;
  onStatusDropdownClose: () => void;
  onStatusChange: (status: JobStatus) => void;
  onEmailClick: (email: FeedEmailData, e: React.MouseEvent) => void;
  onRefreshLastContacted: () => void;
  /** Opens the "all jobs for this customer" browse view (what [workspace →] fires). */
  onViewAllJobs: () => void;
}

const TerminalCustomerCard: React.FC<TerminalCustomerCardProps> = ({
  customer,
  jobEmailsMap,
  updatingLastContactedFor,
  showStatusDropdown,
  onJobClick,
  onDateClick,
  onOpenStatusModal,
  onStatusDropdownClose,
  onStatusChange,
  onEmailClick,
  onRefreshLastContacted,
  onViewAllJobs,
}) => {
  // Jobs collapsed by default to the first N visible; rest via [+N more]
  const [showAllJobs, setShowAllJobs] = useState(false);
  const VISIBLE_JOB_LIMIT = 2;

  const visibleJobs = useMemo(() => {
    if (showAllJobs) return customer.activeJobs;
    return customer.activeJobs.slice(0, VISIBLE_JOB_LIMIT);
  }, [customer.activeJobs, showAllJobs]);

  const hiddenJobCount = Math.max(0, customer.activeJobs.length - VISIBLE_JOB_LIMIT);

  // Customer-level ball state — priority of worst job state dictates the
  // separator accent color. quiet < replied < waiting < stale.
  const customerBall: BallState = useMemo(() => {
    let worst: BallState = 'quiet';
    const rank: Record<BallState, number> = { quiet: 0, replied: 1, waiting: 2, stale: 3 };
    for (const job of customer.activeJobs) {
      const b = ballInCourt(jobEmailsMap[job.id] || []);
      if (rank[b] > rank[worst]) worst = b;
    }
    return worst;
  }, [customer.activeJobs, jobEmailsMap]);

  // Total emails across all jobs (used in the meta line)
  const totalEmails = useMemo(() => {
    return customer.activeJobs.reduce((acc, j) => acc + (jobEmailsMap[j.id]?.length || 0), 0);
  }, [customer.activeJobs, jobEmailsMap]);

  const isRefreshing = updatingLastContactedFor === customer.id;

  return (
    <div className={`term-cust-card term-cust-card--ball-${customerBall}`}>
      {/* ── Separator row: name + last-contact + refresh ──────────────── */}
      <div className="term-cust-card__sep">
        <span className="term-cust-card__sep-line">───</span>
        <span className="term-cust-card__sep-name" title={customer.name}>
          {customer.name.toLowerCase()}
        </span>
        <span className="term-cust-card__sep-line term-cust-card__sep-line--fill" aria-hidden="true" />
        <span className="term-cust-card__sep-last" title={customer.lastContacted ? new Date(customer.lastContacted).toLocaleString() : ''}>
          last: {relTime(customer.lastContacted) || '—'}
        </span>
        <button
          type="button"
          className="term-cust-card__sep-refresh"
          onClick={(e) => { e.stopPropagation(); onRefreshLastContacted(); }}
          disabled={isRefreshing}
          title="Refresh last contact from latest email"
          aria-label="Refresh last contact"
        >
          {isRefreshing ? '…' : '↻'}
        </button>
        <span className="term-cust-card__sep-line">───</span>
      </div>

      {/* ── Contact meta line ─────────────────────────────────────────── */}
      <div className="term-cust-card__meta">
        <span className="term-cust-card__meta-email">{customer.email || '(no email)'}</span>
        <span className="term-cust-card__meta-dot">·</span>
        <span className="term-cust-card__meta-stat">
          {customer.activeJobs.length} job{customer.activeJobs.length !== 1 ? 's' : ''}
        </span>
        <span className="term-cust-card__meta-dot">·</span>
        <span className="term-cust-card__meta-stat">
          {totalEmails} email{totalEmails !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Job rows ──────────────────────────────────────────────────── */}
      <div className="term-cust-card__jobs">
        {visibleJobs.map((job) => (
          <JobLogRow
            key={job.id}
            job={job}
            emails={jobEmailsMap[job.id] || []}
            statusDropdownOpen={showStatusDropdown === job.id}
            onJobClick={onJobClick}
            onDateClick={onDateClick}
            onStatusTriggerClick={(e) => {
              e.stopPropagation();
              if (showStatusDropdown === job.id) {
                onStatusDropdownClose();
              } else {
                onOpenStatusModal(job, e);
              }
            }}
            onStatusDropdownClose={onStatusDropdownClose}
            onStatusChange={onStatusChange}
            onEmailClick={onEmailClick}
          />
        ))}

        {hiddenJobCount > 0 && !showAllJobs && (
          <button
            type="button"
            className="term-cust-card__more"
            onClick={(e) => { e.stopPropagation(); setShowAllJobs(true); }}
          >
            [+ show {hiddenJobCount} more job{hiddenJobCount > 1 ? 's' : ''}]
          </button>
        )}
        {showAllJobs && customer.activeJobs.length > VISIBLE_JOB_LIMIT && (
          <button
            type="button"
            className="term-cust-card__more"
            onClick={(e) => { e.stopPropagation(); setShowAllJobs(false); }}
          >
            [collapse]
          </button>
        )}
      </div>

      {/* ── Action row ────────────────────────────────────────────────── */}
      <div className="term-cust-card__actions">
        <button
          type="button"
          className="term-cust-card__action term-cust-card__action--primary"
          onClick={(e) => { e.stopPropagation(); onViewAllJobs(); }}
          title="Open workspace — browse all jobs for this customer"
        >
          <span className="term-cust-card__action-bracket">[</span>
          <span>workspace →</span>
          <span className="term-cust-card__action-bracket">]</span>
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// One job, rendered as a log-style row with its mini email thread.
// ─────────────────────────────────────────────────────────────────────────

interface JobLogRowProps {
  job: Job;
  emails: FeedEmailData[];
  statusDropdownOpen: boolean;
  onJobClick: (e: React.MouseEvent<HTMLButtonElement>, job: Job) => void;
  onDateClick: (e: React.MouseEvent<HTMLElement>, job: Job) => void;
  onStatusTriggerClick: (e: React.MouseEvent) => void;
  onStatusDropdownClose: () => void;
  onStatusChange: (status: JobStatus) => void;
  onEmailClick: (email: FeedEmailData, e: React.MouseEvent) => void;
}

const JobLogRow: React.FC<JobLogRowProps> = ({
  job,
  emails,
  statusDropdownOpen,
  onJobClick,
  onDateClick,
  onStatusTriggerClick,
  onStatusDropdownClose,
  onStatusChange,
  onEmailClick,
}) => {
  const dismissedAt = parseDismissedAt(job);
  const ball = ballInCourt(emails);
  const due  = formatDue(job.dueDate);
  const unread = countInboundUnread(emails, dismissedAt);

  // ── Dismiss-attention handler ─────────────────────────────────────
  // Click [×] next to the [N] badge → write an ISO timestamp to the
  // job. Card refreshes via the real-time listener; count recomputes
  // to 0. A new inbound email after this point will bring it back.
  const [dismissing, setDismissing] = useState(false);
  const handleDismissAttention = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!job.id || dismissing) return;
    setDismissing(true);
    try {
      await updateDoc(doc(db, 'jobs', job.id), {
        attentionDismissedAt: new Date().toISOString(),
        lastEdited: new Date().toISOString(),
      });
    } catch (err: any) {
      console.error('[TerminalCustomerCard] dismiss attention failed:', err);
      toast.error(err?.message || 'Could not dismiss');
    } finally {
      setDismissing(false);
    }
  };

  // Sort and cap email log to last EMAIL_LOG_LIMIT entries.
  const emailLog = useMemo(() => {
    return [...emails]
      .sort((a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0))
      .slice(0, EMAIL_LOG_LIMIT);
  }, [emails]);

  return (
    <div className={`term-job-row term-job-row--ball-${ball}`}>
      {/* Row 1: glyph + job name + status + due + unread-count */}
      <div className="term-job-row__head">
        <span className="term-job-row__glyph" aria-hidden="true">▸</span>
        <button
          type="button"
          className="term-job-row__name"
          onClick={(e) => { e.stopPropagation(); onJobClick(e, job); }}
          title="Open workspace"
        >
          {job.jobLabel || `Job #${job.id.slice(0, 8)}`}
        </button>

        <span className="term-job-row__sep">·</span>

        <StatusPicker
          job={job}
          open={statusDropdownOpen}
          onTriggerClick={onStatusTriggerClick}
          onClose={onStatusDropdownClose}
          onChange={onStatusChange}
        />

        <span className="term-job-row__sep">·</span>

        <button
          type="button"
          className={`term-job-row__due term-job-row__due--${due.tone}`}
          onClick={(e) => { e.stopPropagation(); onDateClick(e, job); }}
          title="Click to change due date"
        >
          {due.text}
        </button>

        {unread > 0 && (
          <span className="term-job-row__unread-wrap">
            <span
              className={`term-job-row__unread term-job-row__unread--${ball === 'stale' ? 'stale' : 'waiting'}`}
              title={`${unread} inbound message${unread > 1 ? 's' : ''} since your last reply`}
            >
              [{unread}]
            </span>
            <button
              type="button"
              className="term-job-row__unread-dismiss"
              onClick={handleDismissAttention}
              disabled={dismissing}
              title="Dismiss — I've dealt with this out of band"
              aria-label="Dismiss inbound flag"
            >
              [×]
            </button>
          </span>
        )}
      </div>

      {/* Rows 2..N: mini email log */}
      {emailLog.length === 0 ? (
        <div className="term-job-row__log term-job-row__log--empty">
          <span className="term-job-row__log-time">—</span>
          <span className="term-job-row__log-arrow"> </span>
          <span className="term-job-row__log-msg">no emails yet</span>
        </div>
      ) : (
        emailLog.map((email) => {
          const isOutbound = email.folder === 'sent' || email.emailType === 'sent';
          return (
            <div
              key={email.id}
              className={`term-job-row__log term-job-row__log--${isOutbound ? 'out' : 'in'}`}
              onClick={(e) => { e.stopPropagation(); onEmailClick(email, e); }}
              role="button"
              tabIndex={0}
            >
              <span className="term-job-row__log-time">{relTime(email.createdAt)}</span>
              <span className="term-job-row__log-arrow" aria-hidden="true">
                {isOutbound ? '→' : '←'}
              </span>
              <span className="term-job-row__log-msg" title={email.subject || snippetFor(email)}>
                {snippetFor(email)}
              </span>
            </div>
          );
        })
      )}

      {emails.length > EMAIL_LOG_LIMIT && (
        <div className="term-job-row__log term-job-row__log--overflow">
          <span className="term-job-row__log-time"> </span>
          <span className="term-job-row__log-arrow"> </span>
          <span className="term-job-row__log-msg">
            … {emails.length - EMAIL_LOG_LIMIT} more in workspace
          </span>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Inline status picker — text trigger + dropdown list. No color pills.
// ─────────────────────────────────────────────────────────────────────────

interface StatusPickerProps {
  job: Job;
  open: boolean;
  onTriggerClick: (e: React.MouseEvent) => void;
  onClose: () => void;
  onChange: (status: JobStatus) => void;
}

const StatusPicker: React.FC<StatusPickerProps> = ({ job, open, onTriggerClick, onClose, onChange }) => {
  const rootRef = useRef<HTMLSpanElement>(null);

  // Close on outside click — keeps parent state consistent with the UI
  useEffect(() => {
    if (!open) return;
    const handler = (ev: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(ev.target as Node)) onClose();
    };
    // Use a timeout so the trigger's click doesn't immediately close it
    const t = window.setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', handler);
    };
  }, [open, onClose]);

  return (
    <span className="term-status-picker" ref={rootRef} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="term-status-picker__trigger"
        onClick={onTriggerClick}
        title="Change status"
      >
        {statusText(job.status)}
        <span className="term-status-picker__caret">▾</span>
      </button>
      {open && (
        <div className="term-status-picker__menu" role="menu">
          {SELECTABLE_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              role="menuitem"
              className={
                'term-status-picker__item'
                + (s === job.status ? ' term-status-picker__item--active' : '')
              }
              onClick={(e) => {
                e.stopPropagation();
                onChange(s);
                onClose();
              }}
            >
              <span className="term-status-picker__item-glyph">
                {s === job.status ? '>' : ' '}
              </span>
              {statusText(s)}
            </button>
          ))}
        </div>
      )}
    </span>
  );
};

export default TerminalCustomerCard;
